import { createSelector } from '@reduxjs/toolkit';
import * as React from 'react';
import { alphaSort, noop } from 'vet-bones/utils';

import { memoizeSelectorFactory } from 'src/selectors/memoizeSelectorFactory';
import { projectModelsByIdSelector } from 'src/selectors/projectModelsById';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

const sortedModelIdsSelector = memoizeSelectorFactory((projectId: string) =>
  createSelector(projectModelsByIdSelector(projectId), (modelsById) =>
    Object.values(modelsById)
      .sort(alphaSort((model) => model.name.toLowerCase()))
      .map((model) => model.id)
  )
);

const namespacesByModelIdSelector = memoizeSelectorFactory(
  (projectId: string) =>
    createSelector(projectModelsByIdSelector(projectId), (modelsById) =>
      Object.values(modelsById).reduce<Record<string, string>>(
        (acc, model) => ({
          ...acc,
          [model.id]: model.namespace,
        }),
        {}
      )
    )
);

const prefixesByModelIdSelector = memoizeSelectorFactory((projectId: string) =>
  createSelector(projectModelsByIdSelector(projectId), (modelsById) =>
    Object.values(modelsById).reduce<Record<string, string>>(
      (acc, model) => ({
        ...acc,
        [model.id]: model.prefix,
      }),
      {}
    )
  )
);

const shouldPublishPrefixByModelIdSelector = memoizeSelectorFactory(
  (projectId: string) =>
    createSelector(projectModelsByIdSelector(projectId), (modelsById) =>
      Object.values(modelsById).reduce<Record<string, boolean>>(
        (acc, model) => ({
          ...acc,
          [model.id]: model.shouldPublishPrefix,
        }),
        {}
      )
    )
);

const voiceboxInstructionsByModelIdSelector = memoizeSelectorFactory(
  (projectId: string) =>
    createSelector(projectModelsByIdSelector(projectId), (modelsById) =>
      Object.values(modelsById).reduce<Record<string, string>>(
        (acc, model) => ({
          ...acc,
          [model.id]: model.voiceboxInstructions || '',
        }),
        {}
      )
    )
);

export type ProjectModelSettingsValue = {
  sortedModelIds: string[];
  modelNamespaces: Record<string, string>;
  modelPrefixes: Record<string, string>;
  shouldPublishPrefixByModel: Record<string, boolean>;
  voiceboxInstructionsByModel: Record<string, string>;
  handleResetModelSettings: () => void;
  setModelNamespaces: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  setModelPrefixes: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  setShouldPublishPrefixByModel: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  setVoiceboxInstructionsByModel: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
};

export const ProjectModelSettingsContext =
  React.createContext<ProjectModelSettingsValue>({
    sortedModelIds: [],
    modelNamespaces: {},
    modelPrefixes: {},
    shouldPublishPrefixByModel: {},
    voiceboxInstructionsByModel: {},
    handleResetModelSettings: noop,
    setModelNamespaces: noop,
    setModelPrefixes: noop,
    setShouldPublishPrefixByModel: noop,
    setVoiceboxInstructionsByModel: noop,
  });

export const useProjectModelSettings = () => {
  const contextMenu = React.useContext(ProjectModelSettingsContext);
  return contextMenu;
};

export const useProjectModelSettingsValue = (): ProjectModelSettingsValue => {
  const { projectId } = useRouterParams();

  const sortedModelIds = useReduxSelector(sortedModelIdsSelector(projectId));

  const initialNamespacesByModelId = useReduxSelector(
    namespacesByModelIdSelector(projectId)
  );
  const initialPrefixesByModelId = useReduxSelector(
    prefixesByModelIdSelector(projectId)
  );
  const initialSetPrefixSettingByModelId = useReduxSelector(
    shouldPublishPrefixByModelIdSelector(projectId)
  );
  const initialVoiceboxInstructionsByModelId = useReduxSelector(
    voiceboxInstructionsByModelIdSelector(projectId)
  );

  const [modelNamespaces, setModelNamespaces] = React.useState(
    initialNamespacesByModelId
  );
  const [modelPrefixes, setModelPrefixes] = React.useState(
    initialPrefixesByModelId
  );
  const [shouldPublishPrefixByModel, setShouldPublishPrefixByModel] =
    React.useState(initialSetPrefixSettingByModelId);
  const [voiceboxInstructionsByModel, setVoiceboxInstructionsByModel] =
    React.useState(initialVoiceboxInstructionsByModelId);

  const handleResetModelSettings = React.useCallback(() => {
    setModelNamespaces(initialNamespacesByModelId);
    setModelPrefixes(initialPrefixesByModelId);
    setShouldPublishPrefixByModel(initialSetPrefixSettingByModelId);
    setVoiceboxInstructionsByModel(initialVoiceboxInstructionsByModelId);
  }, [
    initialNamespacesByModelId,
    initialPrefixesByModelId,
    initialSetPrefixSettingByModelId,
    initialVoiceboxInstructionsByModelId,
  ]);

  return {
    sortedModelIds,
    modelNamespaces,
    modelPrefixes,
    shouldPublishPrefixByModel,
    voiceboxInstructionsByModel,
    handleResetModelSettings,
    setModelNamespaces,
    setModelPrefixes,
    setShouldPublishPrefixByModel,
    setVoiceboxInstructionsByModel,
  };
};
